import { Icon, cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { PersonaStaleBadge } from "@/features/personas/components/PersonaStaleBadge/PersonaStaleBadge";
import type { PersonaOpportunityPersonaGroup } from "../../../lib/persona-opportunity-row-model.js";

export interface PersonaOpportunityPersonaGroupHeaderProps {
  group: PersonaOpportunityPersonaGroup;
  expanded: boolean;
  onToggle: () => void;
  /** When true, show pains/motivations lists under the header (table view). */
  showProfileLists?: boolean;
}

export function PersonaOpportunityPersonaGroupHeader({
  group,
  expanded,
  onToggle,
  showProfileLists = false,
}: PersonaOpportunityPersonaGroupHeaderProps) {
  const { t } = useTranslation();
  const angleCount = group.opportunities.length;

  return (
    <div className="rounded-md bg-muted/40">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-start gap-3 px-4 py-4 text-left transition-colors hover:bg-muted/60"
      >
        <Icon
          name="chevron-right"
          size="sm"
          className={cn(
            "mt-2.5 shrink-0 text-muted-foreground transition-transform duration-200",
            expanded && "rotate-90",
          )}
          aria-hidden
        />
        <span
          aria-hidden
          className="inline-flex size-[42px] shrink-0 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-100"
        >
          {group.initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-semibold text-foreground">{group.title}</span>
            <span className="rounded-full bg-background px-2 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground ring-1 ring-border">
              {t("personaOpportunities.active.angleCount", { count: angleCount })}
            </span>
            {group.isStale ? <PersonaStaleBadge /> : null}
          </div>
          {group.who ? (
            <p className="mt-1 text-[13px] leading-snug text-muted-foreground">{group.who}</p>
          ) : null}
        </div>
      </button>

      {expanded && showProfileLists && (group.pains.length > 0 || group.motivations.length > 0) ? (
        <div className="grid gap-4 border-t border-border/60 px-4 pb-4 pl-[4.75rem] pt-3 sm:grid-cols-2">
          {group.pains.length > 0 ? (
            <div>
              <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("personaOpportunities.active.painPoints", "Pain points")}
              </p>
              <ul className="space-y-1 text-[12.5px] text-muted-foreground">
                {group.pains.map((pain) => (
                  <li key={pain}>· {pain}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {group.motivations.length > 0 ? (
            <div>
              <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("personaOpportunities.active.motivations", "Motivations")}
              </p>
              <ul className="space-y-1 text-[12.5px] text-muted-foreground">
                {group.motivations.map((motivation) => (
                  <li key={motivation}>· {motivation}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
